import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { Clear, DescriptionOutlined, EventRepeatOutlined, FactCheckOutlined, FilterList, GroupsOutlined, Search, VisibilityOutlined } from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';
import { getMetrics, getPatientsV2, type PatientsV2ListItemResponse } from '../../../../services/patients/patientsV2Service';
import { BrighterGreen, DarkGreen, PrimaryTitleGray, ShadowColor } from '../../../../app/theme/tokens/colors';
import { canReviewPatientsV2, type KpiTone } from './types';
import { usePortalStore } from '../../../../stores/portalStore';
import { KPICards } from '../../components/KPICards';

type Kpi = { label: string; tone: KpiTone };

const columns = [
  { id: 'patient', label: 'Paciente', minWidth: 190 },
  { id: 'cns', label: 'CNS', minWidth: 125 },
  { id: 'urs', label: 'URS solicitante', minWidth: 145 },
  { id: 'crs', label: 'CRS', minWidth: 125 },
  { id: 'requestedAt', label: 'Data da solicitação', minWidth: 165 },
  { id: 'sla', label: 'SLA', minWidth: 125 },
  { id: 'regulatoryStatus', label: 'Situação regulatória', minWidth: 175 },
  { id: 'actions', label: 'Ações', minWidth: 90, align: 'center' as const },
];

const statusTone: Record<string, { color: string; background: string }> = {
  Ativo: { color: '#287a20', background: '#eaf6e6' },
  Aprovado: { color: '#287a20', background: '#eaf6e6' },
  Solicitado: { color: '#1769aa', background: '#eaf4fb' },
  'Em análise': { color: '#1769aa', background: '#eaf4fb' },
  Pendente: { color: '#9a5b00', background: '#fff4dc' },
  'Deferido provisório': { color: '#287a20', background: '#edf7f0' },
  'Indeferimento provisório': { color: '#a86200', background: '#fff1dc' },
  Indeferido: { color: '#b42318', background: '#fcebe8' },
  Inativo: { color: '#b42318', background: '#fcebe8' },
};

const StatusChip = ({ label }: { label: string }) => {
  const colors = statusTone[label] ?? {
    color: '#46554a',
    background: '#eff2f0',
  };
  return (
    <Chip
      label={label || '—'}
      size="small"
      sx={{
        color: colors.color,
        backgroundColor: colors.background,
        fontWeight: 600,
      }}
    />
  );
};

export const PatientsV2List = () => {
  const navigate = useNavigate();
  const currentUser = usePortalStore((state: any) => state.currentUser);
  const canReviewPatients = canReviewPatientsV2(currentUser?.patientV2Profile);
  const [search, setSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [rows, setRows] = useState<PatientsV2ListItemResponse[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const timeout = window.setTimeout(
      async () => {
        setLoading(true);
        setError('');
        try {
          const response = await getPatientsV2({
            page,
            pageSize: rowsPerPage,
            search,
          });
          setRows(Array.isArray(response.data) ? response.data : []);
          setTotalRows(Number(response.rows ?? 0));
        } catch (requestError: any) {
          setRows([]);
          setTotalRows(0);
          setError(requestError?.message ?? 'Não foi possível carregar os pacientes.');
        } finally {
          setLoading(false);
        }
      },
      search.trim() ? 350 : 0
    );

    return () => window.clearTimeout(timeout);
  }, [page, rowsPerPage, search, reloadKey]);

  const clearFilters = () => {
    setSearch('');
    setPage(0);
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100%',
        p: { xs: 2, md: 3 },
        boxSizing: 'border-box',
        backgroundColor: '#ececec',
      }}
    >
      <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'flex-start' }} spacing={1.5} sx={{ mb: 2.5 }}>
          <Box>
            <Typography variant="h4" sx={{ color: PrimaryTitleGray, fontWeight: 700, mb: 0.5 }}>
              Pacientes
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gerencie e acompanhe as solicitações de inscrição e aprovação de pacientes.
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={() => navigate('/patientsv2/new')}
            sx={{
              alignSelf: { xs: 'stretch', sm: 'flex-start' },
              backgroundColor: BrighterGreen,
              '&:hover': { backgroundColor: DarkGreen },
              textTransform: 'none',
              fontWeight: 700,
            }}
          >
            Cadastrar paciente
          </Button>
        </Stack>

        <KPICards />

        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, md: 2 },
            borderRadius: 2,
            boxShadow: `0 2px 10px ${ShadowColor}`,
            overflow: 'visible',
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
            <TextField
              fullWidth
              size="small"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
              placeholder="Buscar paciente por nome"
              inputProps={{ 'aria-label': 'Buscar paciente por nome' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Box
                component="button"
                type="button"
                onClick={clearFilters}
                sx={{
                  border: 0,
                  background: 'transparent',
                  color: BrighterGreen,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  whiteSpace: 'nowrap',
                  px: 1,
                  fontSize: 14,
                }}
              >
                <Clear fontSize="small" /> Limpar filtros
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => setFiltersOpen((open) => !open)}
                sx={{
                  border: `1px solid ${BrighterGreen}`,
                  borderRadius: 1,
                  background: filtersOpen ? '#eef7e9' : 'white',
                  color: DarkGreen,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  whiteSpace: 'nowrap',
                  px: 1.5,
                  py: 0.75,
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                <FilterList fontSize="small" /> Filtros
              </Box>
            </Stack>
          </Stack>

          <Collapse in={filtersOpen} timeout="auto" unmountOnExit>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ pt: 2 }}>
              {['URS Solicitante', 'CRS', 'Situação regulatória'].map((label) => (
                <FormControl key={label} fullWidth size="small" disabled>
                  <InputLabel>{label}</InputLabel>
                  <Select value="" label={label}>
                    <MenuItem value="">Dados não disponíveis</MenuItem>
                  </Select>
                </FormControl>
              ))}
            </Stack>
          </Collapse>

          {error && (
            <Alert
              severity="error"
              sx={{ mt: 2 }}
              action={
                <Box
                  component="button"
                  type="button"
                  onClick={() => setReloadKey((value) => value + 1)}
                  style={{
                    border: 0,
                    background: 'transparent',
                    cursor: 'pointer',
                    fontWeight: 700,
                  }}
                >
                  Tentar novamente
                </Box>
              }
            >
              {error}
            </Alert>
          )}

          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              mt: 2,
              maxHeight: { xs: 520, md: 'calc(100vh - 430px)' },
              border: '1px solid #e6e6e6',
            }}
          >
            <Table stickyHeader size="small" aria-label="Solicitações de pacientes">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      sx={{
                        minWidth: column.minWidth,
                        backgroundColor: '#20362e',
                        color: 'white',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {column.id === 'patient' || column.id === 'regulatoryStatus' ? (
                        <TableSortLabel
                          disabled
                          hideSortIcon
                          sx={{
                            color: 'white !important',
                            '&.Mui-disabled': {
                              color: 'white !important',
                              opacity: 1,
                            },
                          }}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  rows.map((row) =>
                    <TableRow hover key={row.id}>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: PrimaryTitleGray,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.name || '—'}
                      </TableCell>
                      <TableCell>—</TableCell>
                      <TableCell>—</TableCell>
                      <TableCell>—</TableCell>
                      <TableCell>{Intl.DateTimeFormat('pt-BR').format(new Date(row.requestedAt))}</TableCell>
                      <TableCell>—</TableCell>
                      <TableCell>
                        <StatusChip label={row.regulatoryStatus} />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" aria-label={`Visualizar ${row.name}`} onClick={() => navigate(`/patientsv2/${row.id}`)}>
                          <VisibilityOutlined fontSize="small" />
                        </IconButton>
                        {canReviewPatients && ['Em preenchimento', 'Em análise', 'Pendente'].includes(row.regulatoryStatus) && (
                          <IconButton size="small" aria-label={`Analisar ${row.name}`} onClick={() => navigate(`/patientsv2/${row.id}/review`)}>
                            <FactCheckOutlined fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                {!loading && !error && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" sx={{ py: 5 }}>
                      Nenhum paciente encontrado.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalRows}
            page={page}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage="Linhas por página"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </Paper>
      </Box>
    </Box>
  );
};
